import { getCookie } from './cookies';

export type CmsHoroscopeGeneratorTabKey = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface CmsHoroscopeGeneratorSessionState {
    pubDateFrom: string;
    pubDateTo: string;
    timeFrom: string;
    timeTo: string;
    promptTemplate: string;
    selectedExpertIds: number[];
}

const PREFIX = 'cms_horoscope_ai_gen_';

export function cmsHoroscopeGeneratorStorageKey(tab: CmsHoroscopeGeneratorTabKey): string {
    return `${PREFIX}${tab}`;
}

/** Cookie keys that previously seeded generator prompts / authors */
const TAB_COOKIE_PROMPT: Record<CmsHoroscopeGeneratorTabKey, string> = {
    daily: 'horoscopeDailyPrompt',
    weekly: 'horoscopeWeeklyPrompt',
    monthly: 'horoscopeMonthlyPrompt',
    yearly: 'horoscopeYearlyPrompt'
};

const TAB_COOKIE_EXPERTS: Record<CmsHoroscopeGeneratorTabKey, string> = {
    daily: 'horoscopeDailyExperts',
    weekly: 'horoscopeWeeklyExperts',
    monthly: 'horoscopeMonthlyExperts',
    yearly: 'horoscopeYearlyExperts'
};

function safeParseExperts(cookieVal: string | null): number[] {
    if (!cookieVal) return [];
    try {
        const arr = JSON.parse(cookieVal) as unknown;
        if (!Array.isArray(arr)) return [];
        return arr.map(x => Number(x)).filter(id => Number.isFinite(id));
    } catch {
        return [];
    }
}

export function loadCmsHoroscopeGeneratorSession(
    tab: CmsHoroscopeGeneratorTabKey,
    defaults: {
        promptTemplate: string;
        timeFrom: string;
        timeTo: string;
        pubDateFrom: string;
        pubDateTo: string;
    }
): CmsHoroscopeGeneratorSessionState {
    if (typeof window === 'undefined') {
        return {
            pubDateFrom: defaults.pubDateFrom,
            pubDateTo: defaults.pubDateTo,
            timeFrom: defaults.timeFrom,
            timeTo: defaults.timeTo,
            promptTemplate: defaults.promptTemplate,
            selectedExpertIds: []
        };
    }

    const key = cmsHoroscopeGeneratorStorageKey(tab);
    try {
        const raw = sessionStorage.getItem(key);
        if (raw) {
            const parsed = JSON.parse(raw) as Partial<CmsHoroscopeGeneratorSessionState>;
            let expertArr: number[] | undefined;
            if (
                parsed &&
                typeof parsed === 'object' &&
                parsed.selectedExpertIds !== undefined &&
                Array.isArray(parsed.selectedExpertIds)
            ) {
                expertArr = parsed.selectedExpertIds
                    .map(x => Number(x))
                    .filter(id => Number.isFinite(id));
            }

            const pubDateFrom =
                typeof parsed.pubDateFrom === 'string' && parsed.pubDateFrom ? parsed.pubDateFrom : defaults.pubDateFrom;
            const pubDateTo =
                typeof parsed.pubDateTo === 'string' && parsed.pubDateTo ? parsed.pubDateTo : defaults.pubDateTo;

            return {
                pubDateFrom,
                pubDateTo,
                timeFrom:
                    typeof parsed.timeFrom === 'string' && parsed.timeFrom !== undefined
                        ? parsed.timeFrom
                        : defaults.timeFrom,
                timeTo:
                    typeof parsed.timeTo === 'string' && parsed.timeTo !== undefined
                        ? parsed.timeTo
                        : defaults.timeTo,
                promptTemplate:
                    typeof parsed.promptTemplate === 'string' && parsed.promptTemplate.trim() !== ''
                        ? parsed.promptTemplate
                        : defaults.promptTemplate,
                selectedExpertIds: expertArr ?? safeParseExperts(getCookie(TAB_COOKIE_EXPERTS[tab]))
            };
        }
    } catch {
        /* fallback below */
    }

    const coPrompt = getCookie(TAB_COOKIE_PROMPT[tab]);
    const coExperts = safeParseExperts(getCookie(TAB_COOKIE_EXPERTS[tab]));

    return {
        pubDateFrom: defaults.pubDateFrom,
        pubDateTo: defaults.pubDateTo,
        timeFrom: defaults.timeFrom,
        timeTo: defaults.timeTo,
        promptTemplate:
            typeof coPrompt === 'string' && coPrompt.trim() !== ''
                ? coPrompt
                : defaults.promptTemplate,
        selectedExpertIds: coExperts
    };
}

export function saveCmsHoroscopeGeneratorSession(
    tab: CmsHoroscopeGeneratorTabKey,
    state: CmsHoroscopeGeneratorSessionState
): void {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.setItem(cmsHoroscopeGeneratorStorageKey(tab), JSON.stringify(state));
    } catch {
        /* ignore quota / privacy mode */
    }
}
